import React, { FC, useEffect, useState } from 'react';
import { Modal } from 'antd';
import { Contact, ContactPayload } from 'core/contacts/types';
import { MAX_LONG_TEXT_LENGTH } from 'core/constants';
import './contactFormModal.scss';

interface ContactFormModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (values: ContactPayload) => Promise<boolean> | void;
    initialContact?: Contact | null;
    submitting?: boolean;
}

interface FieldErrors {
    name?: string;
    email?: string;
    phoneNumber?: string;
    sortOrder?: string;
    notes?: string;
}

const PHONE_REGEX = /^\+?[0-9\s\-()]+$/;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const clearErrors = (prev: FieldErrors, ...keys: (keyof FieldErrors)[]): FieldErrors => {
    const next = { ...prev };
    keys.forEach((k) => { delete next[k]; });
    return next;
};

const ContactFormModal: FC<ContactFormModalProps> = ({
    open, onClose, onSubmit, initialContact, submitting,
}) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [sortOrder, setSortOrder] = useState<string>('0');
    const [notes, setNotes] = useState('');
    const [errors, setErrors] = useState<FieldErrors>({});

    const isEdit = Boolean(initialContact);

    useEffect(() => {
        if (open) {
            setErrors({});
            if (initialContact) {
                setName(initialContact.name);
                setEmail(initialContact.email);
                setPhoneNumber(initialContact.phoneNumber);
                setSortOrder(String(initialContact.sortOrder ?? 0));
                setNotes(initialContact.notes ?? '');
            } else {
                setName('');
                setEmail('');
                setPhoneNumber('');
                setSortOrder('0');
                setNotes('');
            }
        }
    }, [open, initialContact]);

    const validate = (): FieldErrors => {
        const next: FieldErrors = {};
        const trimmedName = name.trim();
        const trimmedEmail = email.trim();
        const trimmedPhone = phoneNumber.trim();
        const trimmedNotes = notes.trim();

        if (!trimmedName) next.name = 'Name is required';
        else if (trimmedName.length > 255) next.name = 'Name must be 255 characters or fewer';

        if (!trimmedEmail) next.email = 'Email is required';
        else if (!EMAIL_REGEX.test(trimmedEmail)) next.email = 'Enter a valid email';
        else if (trimmedEmail.length > 255) next.email = 'Email must be 255 characters or fewer';

        if (!trimmedPhone) next.phoneNumber = 'Phone number is required';
        else if (!PHONE_REGEX.test(trimmedPhone)) {
            next.phoneNumber = 'Use digits, spaces, dashes, parentheses or a leading +';
        } else if (trimmedPhone.length > 20) {
            next.phoneNumber = 'Phone number must be 20 characters or fewer';
        }

        const sortNum = Number(sortOrder);
        if (sortOrder === '' || Number.isNaN(sortNum) || !Number.isInteger(sortNum)) {
            next.sortOrder = 'Sort order must be a whole number';
        }

        if (trimmedNotes && trimmedNotes.length > 5000) next.notes = 'Notes must be 5000 characters or fewer';

        return next;
    };

    const handleSave = async () => {
        const found = validate();
        if (Object.keys(found).length > 0) {
            setErrors(found);
            return;
        }

        const payload: ContactPayload = {
            name: name.trim(),
            email: email.trim(),
            phoneNumber: phoneNumber.trim(),
            sortOrder: Number(sortOrder),
            notes: notes.trim() ? notes.trim() : null,
        };
        await onSubmit(payload);
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={540}
            className="contact-form-modal"
            centered>
            <div className="contact-form-container">
                <h2 className="heading">{isEdit ? 'Edit Contact' : 'Add Contact'}</h2>

                <div className="edit-field">
                    <h3>Name</h3>
                    <input
                        type="text"
                        className={`edit-input${errors.name ? ' input-error' : ''}`}
                        value={name}
                        onChange={(e) => { setName(e.target.value); setErrors((prev) => clearErrors(prev, 'name')); }}
                        placeholder="e.g. Jane Doe" />
                    {errors.name && <span className="field-error">{errors.name}</span>}
                </div>

                <div className="edit-field">
                    <h3>Email</h3>
                    <input
                        type="email"
                        className={`edit-input${errors.email ? ' input-error' : ''}`}
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); setErrors((prev) => clearErrors(prev, 'email')); }}
                        placeholder="e.g. jane@example.com" />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                </div>

                <div className="edit-field">
                    <h3>Phone Number</h3>
                    <input
                        type="text"
                        className={`edit-input${errors.phoneNumber ? ' input-error' : ''}`}
                        value={phoneNumber}
                        onChange={(e) => { setPhoneNumber(e.target.value); setErrors((prev) => clearErrors(prev, 'phoneNumber')); }}
                        placeholder="e.g. +44 20 1234 5678" />
                    {errors.phoneNumber && <span className="field-error">{errors.phoneNumber}</span>}
                </div>

                <div className="edit-field">
                    <h3>Sort Order</h3>
                    <input
                        type="number"
                        step={1}
                        className={`edit-input${errors.sortOrder ? ' input-error' : ''}`}
                        value={sortOrder}
                        onChange={(e) => { setSortOrder(e.target.value); setErrors((prev) => clearErrors(prev, 'sortOrder')); }}
                        placeholder="0" />
                    <span className="field-hint">Lower numbers appear first.</span>
                    {errors.sortOrder && <span className="field-error">{errors.sortOrder}</span>}
                </div>

                <div className="edit-field">
                    <h3>Notes</h3>
                    <textarea
                        rows={4}
                        className={`edit-input edit-textarea${errors.notes ? ' input-error' : ''}`}
                        value={notes}
                        maxLength={MAX_LONG_TEXT_LENGTH}
                        onChange={(e) => { setNotes(e.target.value); setErrors((prev) => clearErrors(prev, 'notes')); }}
                        placeholder="Optional notes about this contact" />
                    {errors.notes && <span className="field-error">{errors.notes}</span>}
                </div>

                <button
                    type="button"
                    className="update-button"
                    onClick={handleSave}
                    disabled={Boolean(submitting)}>
                    {(() => {
                        if (submitting) return 'Saving…';
                        return isEdit ? 'Save Changes' : 'Add Contact';
                    })()}
                </button>
            </div>
        </Modal>
    );
};

ContactFormModal.defaultProps = {
    initialContact: null,
    submitting: false,
};

export default ContactFormModal;
